/**
 * Formate une durée en secondes vers un affichage lisible.
 * 
 * @param {number} totalSecondes - Durée en secondes
 * @param {object} options
 * @param {boolean} options.avecSecondes - Afficher les secondes (défaut: true)
 * @param {boolean} options.compact - Format compact sans espaces (défaut: false)
 * @returns {string} Durée formatée (ex: "2h 15min 30s", "45s", "1h 00min")
 */
export function formatDuree(totalSecondes, options = {}) {
  const { avecSecondes = true, compact = false } = options;

  if (!totalSecondes || totalSecondes <= 0) return avecSecondes ? '0s' : '0min';

  const heures = Math.floor(totalSecondes / 3600);
  const minutes = Math.floor((totalSecondes % 3600) / 60);
  const secondes = totalSecondes % 60;

  const sep = compact ? '' : ' ';
  const parts = [];

  if (heures > 0) {
    parts.push(`${heures}h`);
    parts.push(`${minutes.toString().padStart(2, '0')}min`);
    if (avecSecondes && secondes > 0) {
      parts.push(`${secondes.toString().padStart(2, '0')}s`);
    }
  } else if (minutes > 0) {
    parts.push(`${minutes}min`);
    if (avecSecondes && secondes > 0) {
      parts.push(`${secondes.toString().padStart(2, '0')}s`);
    }
  } else {
    parts.push(`${secondes}s`);
  }

  return parts.join(sep);
}

/**
 * Formate une durée en minutes (HH:MM) depuis des créneaux planning.
 * Utilisé pour les totaux horaires opérateurs.
 * 
 * @param {number} totalMinutes - Durée en minutes
 * @returns {string} Format "Xh XXmin" (ex: "37h 30min", "8h 00min")
 */
export function formatHeuresMinutes(totalMinutes) {
  if (!totalMinutes || totalMinutes <= 0) return '0h 00min';

  const heures = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);

  return `${heures}h ${minutes.toString().padStart(2, '0')}min`;
}

/**
 * Décompose des secondes en { minutes, secondes } pour les formulaires.
 * 
 * @param {number} totalSecondes
 * @returns {{ minutes: number, secondes: number }}
 */
export function decomposerSecondes(totalSecondes) {
  if (!totalSecondes || totalSecondes <= 0) return { minutes: 0, secondes: 0 };
  return {
    minutes: Math.floor(totalSecondes / 60),
    secondes: totalSecondes % 60
  };
}

/**
 * Compose des minutes + secondes en total secondes pour la sauvegarde.
 * 
 * @param {number} minutes
 * @param {number} secondes
 * @returns {number}
 */
export function composerSecondes(minutes, secondes) {
  return (parseInt(minutes) || 0) * 60 + (parseInt(secondes) || 0);
}

/**
 * Calcule la durée en minutes entre deux heures au format "HH:MM".
 * Utilisé pour les créneaux planning opérateurs.
 * 
 * @param {string} heureDebut - ex: "09:00"
 * @param {string} heureFin - ex: "17:30"
 * @returns {number} Durée en minutes
 */
export function calculerDureeCreneauMinutes(heureDebut, heureFin) {
  const [hd, md] = heureDebut.split(':').map(Number);
  const [hf, mf] = heureFin.split(':').map(Number);
  return (hf * 60 + mf) - (hd * 60 + md);
}
